import React, { useState, useEffect } from 'react';
import { useLoaderData, useSearchParams, useNavigate, useNavigation } from 'react-router-dom';
import { ContractorProfile, Category } from '../types/contractor';
import { getContractors, fetchCategories, fetchContractorFacets, ContractorFacets } from '../api/contractorProfile';
import { getFavoriteContractors } from '../api/userFavoriteContractor';
import { fetchProvinces, fetchTowns, Province, Town } from '../api/locations';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import FilterBar from '../components/FilterBar';
import SidebarFilters from '../components/SidebarFilters';
import SearchResultsSummary from '../components/SearchResultsSummary';
import ContractorSearchResultsCard from '../components/ContractorSearchResultsCard';

// Loader function for React Router
export async function contractorListLoader({ request }: { request: Request }) {
  const url = new URL(request.url);
  const categoryIdsParam = url.searchParams.getAll('categoryIds');
  const provinceId = url.searchParams.get('provinceId') || undefined;
  const townId = url.searchParams.get('townId') || undefined;

  const categoryIds = categoryIdsParam.map(Number).filter(Boolean);

  const filters = {
    categoryIds,
    provinceId: provinceId ? parseInt(provinceId) : undefined,
    townId: townId ? parseInt(townId) : undefined,
  };

  const [contractors, favoriteContractors, provinces, categories, towns, facets] = await Promise.all([
    getContractors({ categoryIds: filters.categoryIds, provinceId: filters.provinceId, townId: filters.townId }),
    getFavoriteContractors().catch(() => []),
    fetchProvinces(),
    fetchCategories(),
    filters.provinceId ? fetchTowns(filters.provinceId) : Promise.resolve([]),
    fetchContractorFacets({ categoryIds: filters.categoryIds, provinceId: filters.provinceId, townId: filters.townId }).catch(() => null),
  ]);

  const contractorsWithFavoriteStatus = contractors.map(contractor => ({
    ...contractor,
    isFavorited: favoriteContractors.some(fav => fav.contractorProfileId === contractor.id)
  }));

  return { contractors: contractorsWithFavoriteStatus, filters, provinces, categories, towns, facets };
}

const ContractorListPage: React.FC = () => {
  const { contractors, filters, provinces, categories, towns, facets } = useLoaderData() as {
    contractors: (ContractorProfile & { isFavorited: boolean })[];
    filters: { categoryIds: number[]; provinceId?: number; townId?: number };
    provinces: Province[];
    categories: Category[];
    towns: Town[];
    facets: ContractorFacets | null;
  };

  // Filter dropdown options to only show those with matching contractors.
  const availableProvinces = facets
    ? provinces.filter(p => facets.provinceIds.includes(p.id) || p.id === filters.provinceId)
    : provinces;
  const availableTowns = facets
    ? towns.filter(t => facets.townIds.includes(t.id) || t.id === filters.townId)
    : towns;

  // Derive available categories from the contractors already in the result set.
  // Always include any already-selected categories so they can be deselected.
  const categoryNamesInResults = React.useMemo(
    () => new Set(contractors.flatMap(cp => cp.categories ?? [])),
    [contractors]
  );
  const availableCategories = categories.filter(
    c => categoryNamesInResults.has(c.name) || filters.categoryIds.includes(c.id)
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const navigation = useNavigation();
  const isLoading = navigation.state === 'loading';

  const handleProvinceChange = (provinceId: number | undefined) => {
    if (provinceId) {
      searchParams.set('provinceId', String(provinceId));
    } else {
      searchParams.delete('provinceId');
    }
    searchParams.delete('townId');
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const handleTownChange = (townId: number | undefined) => {
    if (townId) {
      searchParams.set('townId', String(townId));
    } else {
      searchParams.delete('townId');
    }
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const handleCategoryIdsChange = (categoryIds: number[]) => {
    // Replace all categoryIds params
    const entries: [string, string][] = [];
    searchParams.forEach((value, key) => {
      if (key !== 'categoryIds') entries.push([key, value]);
    });
    const newParams = new URLSearchParams(entries);
    categoryIds.forEach(id => newParams.append('categoryIds', String(id)));
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleClearFilters = () => {
    setSearchParams({});
  };

  const handleToggleFavorite = (contractorId: number, isFavorited: boolean) => {
    console.log(`Contractor ${contractorId} favorite status toggled to ${isFavorited}`);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const selectedCategoryNames = filters.categoryIds
    .map(id => categories.find(c => c.id === id)?.name)
    .filter(Boolean)
    .join(', ');

  return (
    <div className="bg-surface text-on-surface min-h-screen pb-24 md:pb-0">
      <TopAppBar />
      {/* Mobile Header */}
      <header className="bg-surface dark:bg-on-background shadow-sm docked full-width top-0 sticky z-40 transition-colors md:hidden">
        <div className="flex justify-between items-center w-full px-container-margin h-16">
          <div className="flex items-center gap-4">
            <button className="text-primary dark:text-primary-fixed active:scale-95 transition-transform" onClick={handleGoBack}>
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
            <h1 className="font-headline-md text-headline-md-mobile font-bold text-primary dark:text-primary-fixed">Buildon</h1>
          </div>
          <div className="flex items-center">
            <button className="text-primary dark:text-primary-fixed active:scale-95 transition-transform p-2">
              <span className="material-symbols-outlined" data-icon="notifications">notifications</span>
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-container-margin py-stack-lg flex gap-gutter">
        <SidebarFilters
          onClearAll={handleClearFilters}
          onProvinceChange={handleProvinceChange}
          onCategoryIdsChange={handleCategoryIdsChange}
          onTownChange={handleTownChange}

          provinces={availableProvinces}
          categories={availableCategories}
          towns={availableTowns}
          selectedCategoryIds={filters.categoryIds}
          selectedProvinceId={filters.provinceId}
          selectedTownId={filters.townId}
        />
        <section className="flex-1">
          <FilterBar
            onFilterClick={(type) => console.log('Filter clicked:', type)}
            onTuneClick={() => console.log('Tune clicked')}
          />
          <div className="flex items-center justify-between mb-stack-lg">
            <SearchResultsSummary
              count={contractors.length}
              query={selectedCategoryNames || 'All categories'}
              location={
                towns.find(t => t.id === filters.townId)?.name ||
                provinces.find(p => p.id === filters.provinceId)?.name ||
                'All provinces'
              }
              isMobile={true}
            />
            <div className="flex items-center gap-2">
              <span className="text-label-md font-label-md text-on-surface-variant">Sort by:</span>
              <select className="bg-transparent border-none font-label-bold text-label-bold text-primary focus:ring-0 cursor-pointer">
                <option>Top Rated</option>
                <option>Price: Low to High</option>
                <option>Distance</option>
              </select>
            </div>
          </div>

          <div className={`space-y-gutter relative transition-opacity duration-150 ${isLoading ? 'opacity-40 pointer-events-none' : ''}`}>
            {isLoading && (
              <div className="absolute inset-0 flex items-start justify-center pt-16 z-10">
                <span className="material-symbols-outlined animate-spin text-primary text-4xl">progress_activity</span>
              </div>
            )}
            {contractors.length === 0 ? (
              <p className="text-center text-on-surface-variant">No contractors found matching your criteria.</p>
            ) : (
              contractors.map((contractor) => (
                <ContractorSearchResultsCard
                  key={contractor.id}
                  id={String(contractor.id)}
                  name={contractor.companyName || ''}
                  companyName={contractor.companyName || ''}
                  rating={contractor.averageRating || 0}
                  reviews={contractor.reviewCount || 0}
                  imageUrl={contractor.profilePictureUrl || 'https://via.placeholder.com/150'}
                  isVerified={true}
                  price={0}
                  priceUnit=""
                  location={`${contractor.city}, ${contractor.province ?? ''}`}
                  experience={`${contractor.yearsOfExperience} years experience`}
                  specialization={contractor.categories?.join(', ') ?? ''}
                  tier="Elite Tier"
                  isMobile={true}
                  isDesktop={true}
                  emergencyService={false}
                  insuredAndBonded={true}
                />
              ))
            )}
          </div>
        </section>
      </main>
      <BottomNavBar />
    </div>
  );
};

export default ContractorListPage;
